import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { buildPaginationItems, buildPaginationLabel, downloadBlob, getApiErrorMessage } from '../../../helper/table-utils';
import { Httpservice } from '../../../services/httpservice';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';

interface ReadingRow {
  serial_no: number;
  meter_no: string;
  manufacturer_name: string;
  days: ReadingDay[];
}

interface ReadingDay { date: string; reading_kwh: number | null; energy_kwh: number | null; }
interface ReadingDate { value: string; label: string; }
interface BsCalendarCell { bs_date: string; bs_date_label: string; ad_date: string; in_current_month: boolean; is_today: boolean; is_selected: boolean; }
interface BsCalendar { bs_year: number; bs_month: number; bs_month_name: string; cells: BsCalendarCell[]; }

interface Pagination { page_no: number; page_size: number; total_records: number; total_pages: number; }

function todayValue(): string { return new Date().toISOString().slice(0, 10); }
function sevenDaysAgoValue(): string {
  const date = new Date();
  date.setDate(date.getDate() - 6);
  return date.toISOString().slice(0, 10);
}

function formatBsDate(value: string): string {
  const parts = value.split('-');
  return parts.length === 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : value;
}

function parseBsDate(value: string): string {
  const parts = value.split('/');
  return parts.length === 3 ? parts[2] + '-' + parts[1].padStart(2, '0') + '-' + parts[0].padStart(2, '0') : value;
}

@Component({
  selector: 'app-reading-report-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ReportShellComponent, ClickOutsideDirective],
  templateUrl: './reading-report.html',
  styleUrl: './reading-report.scss',
})
export class ReadingReportPage implements OnInit {
  @ViewChild('readingDataFilter') private readonly readingDataFilter?: ElementRef<HTMLDetailsElement>;

  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl = new FormControl(15, { nonNullable: true });
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected errorMessage = '';
  protected rows: ReadingRow[] = [];
  protected dates: ReadingDate[] = [];
  protected readonly readingDataOptions = [
    { value: 'reading', label: 'Reading (kWh)' },
    { value: 'energy', label: 'Energy (kWh)' },
  ];
  protected selectedReadingData = new Set<string>(['energy']);
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: BsCalendar | null = null;
  protected activeBsDateControl: 'from_date' | 'to_date' = 'from_date';
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  protected pagination: Pagination = { page_no: 1, page_size: 15, total_records: 0, total_pages: 0 };
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(private readonly fb: UntypedFormBuilder, private readonly httpSvc: Httpservice, private readonly preferencesSvc: AppPreferencesService) {
    this.filtersForm = this.fb.group({ from_date: [sevenDaysAgoValue()], to_date: [todayValue()], search: [''] });
  }

  async ngOnInit(): Promise<void> {
    this.filtersForm.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadReport(1));
    this.pageSizeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadReport(1));
    this.listenToDatePreferenceChanges();
    if (this.isBsDateMode) {
      await this.convertDateControls();
      return;
    }
    this.loadReport();
  }

  protected handleMenuToggle(): void { this.mobileSidebarOpen = !this.mobileSidebarOpen; }
  protected closeMobileSidebar(): void { this.mobileSidebarOpen = false; }
  protected clearFilters(): void {
    this.selectedReadingData = new Set(['energy']);
    this.filtersForm.patchValue({ from_date: sevenDaysAgoValue(), to_date: todayValue(), search: '' }, { emitEvent: false });
    if (this.isBsDateMode) {
      void this.convertDateControls();
      return;
    }
    this.loadReport(1);
  }
  protected goToPage(pageNo: number): void { if (pageNo >= 1 && pageNo <= this.pagination.total_pages && pageNo !== this.pagination.page_no) this.loadReport(pageNo); }
  protected previousPage(): void { this.goToPage(this.pagination.page_no - 1); }
  protected nextPage(): void { this.goToPage(this.pagination.page_no + 1); }
  protected getPaginationLabel(): string { return buildPaginationLabel(this.pagination, 'No records', 'Showing', 'records'); }
  protected getPaginationItems(): number[] { return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no); }
  protected trackBySerial(_: number, row: ReadingRow): number { return row.serial_no; }
  protected formatNumber(value: number | null): string { return value === null ? '-' : new Intl.NumberFormat(undefined, { maximumFractionDigits: 5 }).format(value); }
  protected getSelectedReadingDataLabel(): string {
    const selected = this.readingDataOptions.filter(option => this.selectedReadingData.has(option.value)).map(option => option.label);
    return selected.length ? selected.join(', ') : 'Select data';
  }
  protected isReadingDataSelected(value: string): boolean { return this.selectedReadingData.has(value); }
  protected toggleReadingData(value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.selectedReadingData.add(value); else this.selectedReadingData.delete(value);
    this.selectedReadingData = new Set(this.selectedReadingData);
    this.loadReport(1);
  }
  protected showReading(): boolean { return this.selectedReadingData.has('reading'); }
  protected showEnergy(): boolean { return this.selectedReadingData.has('energy'); }
  @HostListener('document:click', ['$event'])
  protected closeReadingDataFilterOnOutsideClick(event: Event): void {
    const filterElement = this.readingDataFilter?.nativeElement;
    if (filterElement && event.target instanceof Node && !filterElement.contains(event.target)) {
      filterElement.open = false;
    }
  }
  protected get isBsDateMode(): boolean { return this.preferencesSvc.showBsDate; }
  protected getDateInputType(): 'date' | 'text' { return this.isBsDateMode ? 'text' : 'date'; }
  protected openBsDatePicker(controlName: 'from_date' | 'to_date'): void {
    if (!this.isBsDateMode) return;
    if (this.bsCalendarOpen && this.activeBsDateControl === controlName) {
      this.closeBsDatePicker();
      return;
    }
    this.activeBsDateControl = controlName;
    void this.loadBsCalendar(this.getDateControlValue(controlName), 'BS');
  }
  protected closeBsDatePicker(): void { this.bsCalendarOpen = false; }
  protected selectBsCalendarCell(cell: BsCalendarCell): void {
    if (!cell.bs_date) return;
    this.closeBsDatePicker();
    this.filtersForm.patchValue({ [this.activeBsDateControl]: formatBsDate(cell.bs_date) });
  }
  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !offset) return;
    let month = this.bsCalendarMonth.bs_month + offset;
    let year = this.bsCalendarMonth.bs_year;
    if (month < 1) { month = 12; year -= 1; }
    if (month > 12) { month = 1; year += 1; }
    void this.loadBsCalendar(`${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01`, 'BS');
  }
  protected exportToExcel(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.httpSvc.downloadfile(AuthEndPoints.EXPORT_READING_REPORT, ApiMethod.POST, this.buildPayload()).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isExporting = false; this.cdr.markForCheck(); })).subscribe({ next: blob => downloadBlob(blob, 'reading_report.xlsx'), error: error => this.errorMessage = getApiErrorMessage(error) || 'Failed to export Reading Report.' });
  }
  private loadReport(pageNo = this.pagination.page_no): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.httpSvc.requestCall(AuthEndPoints.GET_READING_REPORT, ApiMethod.POST, this.buildPayload(pageNo), false, { suppressToast: true }).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => { this.isLoading = false; this.cdr.markForCheck(); })).subscribe({ next: response => { const value = response?.value ?? {}; this.rows = value.rows ?? []; this.dates = value.dates ?? []; this.pagination = value.pagination ?? this.pagination; if (this.isBsDateMode) { void this.resolveBsTableDateLabels(); } }, error: error => { this.rows = []; this.dates = []; this.errorMessage = getApiErrorMessage(error) || 'Failed to load Reading Report.'; } });
  }
  private formatBsTableDate(value: string): string {
    const normalized = value.trim().replace(/\//g, '-');
    const parts = normalized.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
    }
    return normalized;
  }

  private async resolveBsTableDateLabels(): Promise<void> {
    const reportDates = [...this.dates];
    const convertedLabels = await Promise.all(reportDates.map(async (reportDate) => {
      try {
        const response = await firstValueFrom(this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          { data_date: reportDate.value, bs_ad_key: 'BS', source_bs_ad_key: 'AD' },
          false,
          { suppressToast: true },
        ));
        return this.formatBsTableDate(response?.value?.selected_date_label || response?.value?.bs_date_label || reportDate.label);
      } catch {
        return reportDate.label;
      }
    }));

    this.dates = reportDates.map((reportDate, index) => ({
      ...reportDate,
      label: convertedLabels[index] ?? reportDate.label,
    }));
    this.cdr.markForCheck();
  }

  private buildPayload(pageNo = this.pagination.page_no): object {
    const value = this.filtersForm.getRawValue();
    return { from_date: this.toApiDateValue(value.from_date), to_date: this.toApiDateValue(value.to_date), search: value.search?.trim() || null, reading_data: [...this.selectedReadingData], bs_ad_key: this.isBsDateMode ? 'BS' : 'AD', page_no: pageNo, page_size: this.pageSizeControl.value };
  }

  private getDateControlValue(controlName: 'from_date' | 'to_date'): string {
    const value = this.filtersForm.get(controlName)?.value;
    return typeof value === 'string' ? value : '';
  }

  private listenToDatePreferenceChanges(): void {
    if (typeof window === 'undefined') return;
    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name === 'showBsDate') {
          this.closeBsDatePicker();
          void this.convertDateControls();
        }
      });
  }

  private async convertDateControls(): Promise<void> {
    const targetMode = this.isBsDateMode ? 'BS' : 'AD';
    const sourceMode = targetMode === 'BS' ? 'AD' : 'BS';
    const convertedEntries = await Promise.all((['from_date', 'to_date'] as const).map(async (controlName) => {
      const value = this.getDateControlValue(controlName);
      if (!value) return null;
      try {
        const response = await firstValueFrom(this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: sourceMode === 'BS' ? parseBsDate(value) : value,
            bs_ad_key: targetMode,
            source_bs_ad_key: sourceMode,
          },
          false,
          { suppressToast: true },
        ));
        const result = response?.value;
        const nextValue = targetMode === 'BS' ? result?.bs_date_label : result?.data_date;
        return typeof nextValue === 'string' ? [controlName, nextValue] as const : null;
      } catch { return null; }
    }));
    const patch = Object.fromEntries(convertedEntries.filter((entry): entry is readonly ['from_date' | 'to_date', string] => entry !== null));
    if (Object.keys(patch).length) this.filtersForm.patchValue(patch, { emitEvent: false });
    this.loadReport(1);
  }

  private toApiDateValue(value: unknown): string | null {
    if (typeof value !== 'string' || !value) return null;
    return this.isBsDateMode ? parseBsDate(value) : value;
  }

  private async loadBsCalendar(dateValue: string, bsAdKey: 'AD' | 'BS'): Promise<void> {
    if (!dateValue) return;
    this.bsCalendarOpen = true;
    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(this.httpSvc.requestCall(AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR, ApiMethod.POST, { data_date: bsAdKey === 'BS' ? parseBsDate(dateValue) : dateValue, bs_ad_key: bsAdKey }, false, { suppressToast: true }));
      const value = response?.value ?? {};
      this.bsCalendarMonth = {
        bs_year: Number(value.bs_year) || 0,
        bs_month: Number(value.bs_month) || 0,
        bs_month_name: value.bs_month_name || '',
        cells: Array.isArray(value.cells) ? value.cells : [],
      };
    } catch { this.bsCalendarMonth = null; }
    finally { this.bsCalendarLoading = false; this.cdr.markForCheck(); }
  }
}
